/**
 * Captures Google click identifiers and campaign parameters on the first
 * page of a visit and holds them for the session, so they can be written
 * onto the Firestore submission record.
 *
 * Why this matters: a booking is recorded in Firestore, but Google Ads only
 * ever sees the in-browser conversion. Storing the gclid alongside the lead
 * is what makes offline conversion import possible later — uploading
 * "this lead became a client" back to Ads so bidding optimises toward
 * revenue instead of raw form fills. It cannot be reconstructed after the
 * fact, so it is worth capturing before it is needed.
 */

const STORAGE_KEY = "effigen_attribution";

const CLICK_IDS = ["gclid", "gbraid", "wbraid"];
const CAMPAIGN = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

const read = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY)) || null;
  } catch {
    return null;
  }
};

/**
 * Call once on app boot. Only writes when the current URL actually carries
 * attribution, so an internal navigation never overwrites the values the
 * visitor originally arrived with.
 */
export const captureAttribution = () => {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);
  const captured = {};

  [...CLICK_IDS, ...CAMPAIGN].forEach((key) => {
    const value = params.get(key);
    if (value) captured[key] = value;
  });

  if (Object.keys(captured).length === 0) return;

  captured.landingPage = window.location.pathname;
  captured.referrer = document.referrer || null;
  captured.capturedAt = new Date().toISOString();

  try {
    // First touch wins: don't let a later ?utm_ overwrite the original click.
    if (!read()) {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(captured));
    }
  } catch {
    /* private mode / storage disabled — attribution is best-effort */
  }
};

/** Returns {} when nothing was captured, so it is always safe to spread. */
export const getAttribution = () => read() || {};
